/**
 * اندیکاتورهای تحلیل تکنیکال
 * ============================
 * عمداً **بدون هیچ وابستگی** و جدا از کامپوننت نمودار نوشته شده‌اند.
 *
 * دلیلش این است که ریاضیاتِ اینها همان چیزی است که می‌تواند بی‌سروصدا غلط
 * باشد: یک اشتباه در «میانگین متحرک نمایی» عددی می‌سازد که **قابل‌قبول به
 * نظر می‌رسد** ولی با هیچ صرافی دیگری نمی‌خواند، و کاربر بر اساسش معامله
 * می‌کند. چون اینجا از رابط کاربری جدا هستند، می‌شود مستقیم و عددبه‌عدد
 * آزمودشان — و آزموده شده‌اند (`frontend/tests/indicators.mjs`).
 *
 * قرارداد مشترک همه توابع: خروجی **هم‌طول ورودی** است و مقدارهای گرم‌شدن
 * اولیه `null`‌اند، نه صفر و نه حذف‌شده. اگر صفر می‌گذاشتیم، نمودار یک خط
 * از صفر بالا می‌کشید؛ اگر حذف می‌کردیم، هم‌ترازی زمانی به هم می‌ریخت.
 */

export type Candle = {
  time: number
  open: number
  high: number
  low: number
  close: number
  volume: number
}

export type Series = (number | null)[]

/** میانگین متحرک ساده */
export function sma(values: number[], period: number): Series {
  const out: Series = new Array(values.length).fill(null)
  if (period <= 0) return out
  let sum = 0
  for (let i = 0; i < values.length; i++) {
    sum += values[i]
    if (i >= period) sum -= values[i - period]
    if (i >= period - 1) out[i] = sum / period
  }
  return out
}

/**
 * میانگین متحرک نمایی.
 *
 * نقطه شروع، میانگین ساده `period` مقدار اول است — نه خودِ مقدار اول.
 * شروع از مقدار اول باعث می‌شود ده‌ها کندلِ ابتدایی به‌شکل محسوسی کج باشند.
 */
export function ema(values: number[], period: number): Series {
  const out: Series = new Array(values.length).fill(null)
  if (period <= 0 || values.length < period) return out
  const k = 2 / (period + 1)
  let prev = 0
  for (let i = 0; i < period; i++) prev += values[i]
  prev /= period
  out[period - 1] = prev
  for (let i = period; i < values.length; i++) {
    prev = values[i] * k + prev * (1 - k)
    out[i] = prev
  }
  return out
}

/** باندهای بولینگر — میانگین ساده ± ضریبی از انحراف معیار */
export function bollinger(values: number[], period = 20, mult = 2) {
  const mid = sma(values, period)
  const upper: Series = new Array(values.length).fill(null)
  const lower: Series = new Array(values.length).fill(null)
  for (let i = period - 1; i < values.length; i++) {
    const m = mid[i]
    if (m == null) continue
    let acc = 0
    for (let j = i - period + 1; j <= i; j++) acc += (values[j] - m) ** 2
    const sd = Math.sqrt(acc / period)
    upper[i] = m + mult * sd
    lower[i] = m - mult * sd
  }
  return { upper, middle: mid, lower }
}

/**
 * شاخص قدرت نسبی (RSI) — با هموارسازی وایلدر.
 *
 * هموارسازی وایلدر با «میانگین ساده متحرک» فرق دارد و همان است که همه
 * پلتفرم‌های تحلیلی استفاده می‌کنند. اگر ساده حساب می‌کردیم، عدد ما با
 * چیزی که کاربر در تریدینگ‌ویو می‌بیند نمی‌خواند.
 */
export function rsi(closes: number[], period = 14): Series {
  const out: Series = new Array(closes.length).fill(null)
  if (closes.length <= period) return out
  let gain = 0
  let loss = 0
  for (let i = 1; i <= period; i++) {
    const d = closes[i] - closes[i - 1]
    if (d >= 0) gain += d
    else loss -= d
  }
  gain /= period
  loss /= period
  out[period] = loss === 0 ? 100 : 100 - 100 / (1 + gain / loss)
  for (let i = period + 1; i < closes.length; i++) {
    const d = closes[i] - closes[i - 1]
    gain = (gain * (period - 1) + (d > 0 ? d : 0)) / period
    loss = (loss * (period - 1) + (d < 0 ? -d : 0)) / period
    out[i] = loss === 0 ? 100 : 100 - 100 / (1 + gain / loss)
  }
  return out
}

/**
 * مکدی (MACD).
 *
 * خط سیگنال، میانگین نماییِ **خودِ خط مکدی** است. نکته ظریف: خط مکدی در
 * ابتدا `null` دارد و اگر آن null‌ها را صفر فرض کنیم، سیگنال برای ده‌ها
 * کندل غلط می‌شود. پس فقط روی بخش معتبر حساب می‌شود و بعد دوباره هم‌تراز
 * می‌گردد.
 */
export function macd(closes: number[], fast = 12, slow = 26, signalPeriod = 9) {
  const ef = ema(closes, fast)
  const es = ema(closes, slow)
  const line: Series = new Array(closes.length).fill(null)
  for (let i = 0; i < closes.length; i++) {
    if (ef[i] != null && es[i] != null) line[i] = (ef[i] as number) - (es[i] as number)
  }
  const firstIdx = line.findIndex((v) => v != null)
  const signal: Series = new Array(closes.length).fill(null)
  const hist: Series = new Array(closes.length).fill(null)
  if (firstIdx >= 0) {
    const dense = line.slice(firstIdx) as number[]
    const sig = ema(dense, signalPeriod)
    for (let i = 0; i < sig.length; i++) {
      const v = sig[i]
      if (v == null) continue
      const at = firstIdx + i
      signal[at] = v
      hist[at] = (line[at] as number) - v
    }
  }
  return { line, signal, hist }
}

/** میانگین وزنی حجم — از ابتدای داده انباشته می‌شود */
export function vwap(candles: Candle[], sessionSeconds = 86400): Series {
  const out: Series = new Array(candles.length).fill(null)
  let pv = 0
  let vol = 0
  let session = -1
  for (let i = 0; i < candles.length; i++) {
    const c = candles[i]
    // VWAP در هر «نشست» از صفر شروع می‌شود — برای بازار رمزارز یعنی هر روز.
    //
    // اگر از اولین کندلِ بارگذاری‌شده جمع می‌زدیم، عددش به این وابسته بود
    // که چند کندل گرفته‌ایم؛ با هر بار تازه‌شدن داده کمی جابه‌جا می‌شد و با
    // هیچ پلتفرم دیگری هم نمی‌خواند.
    const s = sessionSeconds > 0 ? Math.floor(c.time / sessionSeconds) : 0
    if (s !== session) { session = s; pv = 0; vol = 0 }
    const tp = (c.high + c.low + c.close) / 3
    pv += tp * c.volume
    vol += c.volume
    out[i] = vol > 0 ? pv / vol : null
  }
  return out
}

export function toLine(candles: Candle[], values: Series) {
  const out: { time: number; value: number }[] = []
  for (let i = 0; i < candles.length; i++) {
    const v = values[i]
    if (v == null || !Number.isFinite(v)) continue
    out.push({ time: candles[i].time, value: v })
  }
  return out
}

/* ══════════════════════════════════════════════════════════════════════════
 *  اندیکاتورهای گروه دوم
 *  ---------------------
 *  همان قرارداد بالا: خروجی هم‌طول ورودی، دوره گرم‌شدن `null`.
 * ═══════════════════════════════════════════════════════════════════════ */

/**
 * هایکن آشی.
 *
 * شمع‌هایی که نوسان ریز را صاف می‌کنند و روند را واضح‌تر نشان می‌دهند.
 * **قیمت واقعی نیستند** — قیمت بسته هایکن آشی میانگین چهار عدد است، نه
 * قیمتی که کسی معامله کرده. برای همین در رابط کاربری صراحتاً گفته می‌شود.
 */
export function heikinAshi(candles: Candle[]): Candle[] {
  const out: Candle[] = []
  let prevO = 0
  let prevC = 0
  for (let i = 0; i < candles.length; i++) {
    const c = candles[i]
    const close = (c.open + c.high + c.low + c.close) / 4
    const open = i === 0 ? (c.open + c.close) / 2 : (prevO + prevC) / 2
    out.push({
      time: c.time, open, close,
      high: Math.max(c.high, open, close),
      low: Math.min(c.low, open, close),
      volume: c.volume,
    })
    prevO = open
    prevC = close
  }
  return out
}

/** دامنه واقعی هر کندل (True Range) */
export function trueRange(candles: Candle[]): number[] {
  return candles.map((c, i) => {
    if (i === 0) return c.high - c.low
    const pc = candles[i - 1].close
    return Math.max(c.high - c.low, Math.abs(c.high - pc), Math.abs(c.low - pc))
  })
}

/**
 * ATR — میانگین دامنه واقعی، با هموارسازی وایلدر (نه میانگین ساده).
 * معیار نوسان؛ در تعیین حد ضرر زیاد استفاده می‌شود.
 */
export function atr(candles: Candle[], period = 14): Series {
  const out: Series = new Array(candles.length).fill(null)
  if (period <= 0 || candles.length < period) return out
  const tr = trueRange(candles)
  let sum = 0
  for (let i = 0; i < period; i++) sum += tr[i]
  let prev = sum / period
  out[period - 1] = prev
  for (let i = period; i < candles.length; i++) {
    prev = (prev * (period - 1) + tr[i]) / period
    out[i] = prev
  }
  return out
}

/** بیشترین سقف و کمترین کف در پنجره‌ای به طول `period` */
function extremes(candles: Candle[], period: number, i: number) {
  let hi = -Infinity
  let lo = Infinity
  for (let j = i - period + 1; j <= i; j++) {
    if (candles[j].high > hi) hi = candles[j].high
    if (candles[j].low < lo) lo = candles[j].low
  }
  return { hi, lo }
}

/**
 * استوکاستیک.
 *
 * `%K` جای قیمت بسته را در دامنه اخیر می‌گوید و `%D` میانگین آن است.
 * وقتی سقف و کف پنجره یکی باشند (بازار کاملاً بی‌حرکت)، مخرج صفر می‌شود؛
 * در آن حالت ۵۰ برمی‌گردد، نه بی‌نهایت.
 */
export function stoch(candles: Candle[], period = 14, smoothK = 3, smoothD = 3) {
  const n = candles.length
  const rawK: Series = new Array(n).fill(null)
  for (let i = period - 1; i < n; i++) {
    const { hi, lo } = extremes(candles, period, i)
    rawK[i] = hi === lo ? 50 : ((candles[i].close - lo) / (hi - lo)) * 100
  }
  const k = smoothSeries(rawK, smoothK)
  const d = smoothSeries(k, smoothD)
  return { k, d }
}

/** میانگین ساده روی سری‌ای که ابتدایش `null` دارد */
function smoothSeries(src: Series, period: number): Series {
  const out: Series = new Array(src.length).fill(null)
  if (period <= 1) return src.slice()
  let sum = 0
  let count = 0
  for (let i = 0; i < src.length; i++) {
    const v = src[i]
    if (v == null) { sum = 0; count = 0; continue }
    sum += v
    count++
    if (count > period) { const old = src[i - period]; if (old != null) sum -= old; count = period }
    if (count === period) out[i] = sum / period
  }
  return out
}

/** شاخص کانال کالا (CCI) */
export function cci(candles: Candle[], period = 20): Series {
  const n = candles.length
  const out: Series = new Array(n).fill(null)
  if (period <= 0 || n < period) return out
  const tp = candles.map((c) => (c.high + c.low + c.close) / 3)
  for (let i = period - 1; i < n; i++) {
    let mean = 0
    for (let j = i - period + 1; j <= i; j++) mean += tp[j]
    mean /= period
    let dev = 0
    for (let j = i - period + 1; j <= i; j++) dev += Math.abs(tp[j] - mean)
    dev /= period
    out[i] = dev === 0 ? 0 : (tp[i] - mean) / (0.015 * dev)
  }
  return out
}

/** ویلیامز %R — همان منطق استوکاستیک، ولی از سقف به پایین (۰ تا ۱۰۰−) */
export function williamsR(candles: Candle[], period = 14): Series {
  const n = candles.length
  const out: Series = new Array(n).fill(null)
  for (let i = period - 1; i < n; i++) {
    const { hi, lo } = extremes(candles, period, i)
    out[i] = hi === lo ? -50 : ((hi - candles[i].close) / (hi - lo)) * -100
  }
  return out
}

/**
 * حجم متوازن (OBV).
 *
 * حجم روزهای صعودی جمع و حجم روزهای نزولی کم می‌شود. عددِ مطلقش معنا ندارد؛
 * جهتش مهم است.
 */
export function obv(candles: Candle[]): Series {
  const out: Series = new Array(candles.length).fill(null)
  if (!candles.length) return out
  let acc = 0
  out[0] = 0
  for (let i = 1; i < candles.length; i++) {
    const d = candles[i].close - candles[i - 1].close
    acc += d > 0 ? candles[i].volume : d < 0 ? -candles[i].volume : 0
    out[i] = acc
  }
  return out
}

/**
 * شاخص جریان نقدینگی (MFI) — «RSIِ حجمی».
 */
export function mfi(candles: Candle[], period = 14): Series {
  const n = candles.length
  const out: Series = new Array(n).fill(null)
  if (period <= 0 || n <= period) return out
  const tp = candles.map((c) => (c.high + c.low + c.close) / 3)
  for (let i = period; i < n; i++) {
    let pos = 0
    let neg = 0
    for (let j = i - period + 1; j <= i; j++) {
      const flow = tp[j] * candles[j].volume
      if (tp[j] > tp[j - 1]) pos += flow
      else if (tp[j] < tp[j - 1]) neg += flow
    }
    out[i] = neg === 0 ? 100 : 100 - 100 / (1 + pos / neg)
  }
  return out
}

/**
 * ابر ایچیموکو.
 *
 * دو خط از میانگین سقف/کف (تنکان و کیجون)، دو مرز ابر که **۲۶ کندل جلوتر**
 * کشیده می‌شوند، و یک خط تأخیری که ۲۶ کندل عقب‌تر می‌رود.
 *
 * نکته عددی: تریدینگ‌ویو ابر را با جابه‌جایی **۲۵** رسم می‌کند نه ۲۶ (در
 * پاین‌اسکریپت `offset = displacement - 1`). اگر ۲۶ می‌گذاشتیم، ابر و خط
 * تأخیریِ ما یک کندل با آنچه کاربر در تریدینگ‌ویو دیده فرق می‌کرد.
 *
 * نکته پیاده‌سازی: چون روی نمودار نمی‌شود جلوتر از آخرین کندل چیزی کشید،
 * جابه‌جایی «به آینده» داخل خودِ آرایه انجام می‌شود؛ یعنی مقدارِ خانه i از
 * محاسبه خانه i−۲۶ می‌آید. ۲۶ کندل آخرِ ابر — که در تریدینگ‌ویو در فضای
 * خالی سمت راست دیده می‌شود — اینجا رسم نمی‌شود.
 */
export function ichimoku(candles: Candle[], p1 = 9, p2 = 26, p3 = 52, shift = 25) {
  const n = candles.length
  const mid = (period: number): Series => {
    const out: Series = new Array(n).fill(null)
    for (let i = period - 1; i < n; i++) {
      const { hi, lo } = extremes(candles, period, i)
      out[i] = (hi + lo) / 2
    }
    return out
  }
  const tenkan = mid(p1)
  const kijun = mid(p2)
  const baseB = mid(p3)

  const spanA: Series = new Array(n).fill(null)
  const spanB: Series = new Array(n).fill(null)
  for (let i = 0; i < n; i++) {
    const src = i - shift
    if (src < 0) continue
    const t = tenkan[src]
    const k = kijun[src]
    spanA[i] = t == null || k == null ? null : (t + k) / 2
    spanB[i] = baseB[src] ?? null
  }
  const chikou: Series = new Array(n).fill(null)
  for (let i = 0; i + shift < n; i++) chikou[i] = candles[i + shift].close

  return { tenkan, kijun, spanA, spanB, chikou }
}

/**
 * سهمی سار (Parabolic SAR).
 *
 * نقطه‌چینی که همیشه **طرف مخالف قیمت** است و با برخورد قیمت، سمت عوض
 * می‌کند. ضریب شتاب از ۰٫۰۲ شروع می‌شود و تا ۰٫۲ بالا می‌رود.
 */
export function psar(candles: Candle[], step = 0.02, max = 0.2): Series {
  const n = candles.length
  const out: Series = new Array(n).fill(null)
  if (n < 2) return out
  let bull = candles[1].close >= candles[0].close
  let af = step
  let ep = bull ? candles[0].high : candles[0].low
  let sar = bull ? candles[0].low : candles[0].high

  for (let i = 1; i < n; i++) {
    sar += af * (ep - sar)
    const p1 = candles[i - 1]
    const p2 = candles[i - 2] ?? p1
    if (bull) {
      sar = Math.min(sar, p1.low, p2.low)
      if (candles[i].low < sar) {
        bull = false; sar = ep; ep = candles[i].low; af = step
      } else if (candles[i].high > ep) {
        ep = candles[i].high; af = Math.min(af + step, max)
      }
    } else {
      sar = Math.max(sar, p1.high, p2.high)
      if (candles[i].high > sar) {
        bull = true; sar = ep; ep = candles[i].high; af = step
      } else if (candles[i].low < ep) {
        ep = candles[i].low; af = Math.min(af + step, max)
      }
    }
    out[i] = sar
  }
  return out
}
